import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { btn } from "./actions";
import { Glyph } from "./icons";
import { Tooltip } from "./Tooltip";
import "./panel-resize.css";

export function usePanelWidth(key: string, initial: number, min: number, max: number) {
  const [width, setWidth] = useState(() => {
    try { const saved = Number(localStorage.getItem(key)); return saved >= min && saved <= max ? saved : initial; } catch { return initial; }
  });
  useEffect(() => {
    const timer = setTimeout(() => { try { localStorage.setItem(key, String(width)); } catch { /* Optional preference storage. */ } }, 150);
    return () => clearTimeout(timer);
  }, [key, width]);
  return { width, setWidth, min, max, initial, style: { "--panel-width": `${width}px` } as CSSProperties };
}

type WidthState = ReturnType<typeof usePanelWidth>;
/** `snap` maps a requested width to an allowed one, e.g. collapsing the navigation to its icon rail once its labels
 *  no longer fit. A drag passes the raw width from its start; keys pass one step from the current width. */
export type PanelSnap = (next: number, current: number, source: "pointer" | "key") => number;
export function PanelResize({ panel, side, label, snap, step = 16, pixelsPerUnit, valueText }: {
  panel: WidthState; side: "left" | "right"; label: string; snap?: PanelSnap;
  step?: number; pixelsPerUnit?: () => number; valueText?: string;
}) {
  const drag = useRef<{ x: number; width: number; scale: number } | null>(null);
  const frame = useRef<number | null>(null);
  const pending = useRef<number | null>(null);
  useEffect(() => () => { if (frame.current !== null) cancelAnimationFrame(frame.current); }, []);
  const direction = side === "right" ? 1 : -1;
  const bound = (value: number) => Math.max(panel.min, Math.min(panel.max, value));
  const clamp = (value: number, current: number, source: "pointer" | "key") => bound(snap ? snap(bound(value), current, source) : value);
  const flush = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const next = pending.current;
    pending.current = null;
    if (next !== null) panel.setWidth((current) => clamp(next, current, "pointer"));
  };
  const finish = (event: PointerEvent<HTMLDivElement>) => {
    flush();
    drag.current = null;
    event.currentTarget.removeAttribute("data-dragging");
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return <div className={`celia-panel-resize is-${side}`} role="separator" tabIndex={0}
    aria-label={label} aria-orientation="vertical" aria-valuemin={panel.min} aria-valuemax={panel.max} aria-valuenow={Math.round(panel.width)}
    aria-valuetext={valueText}
    title="Drag to resize. Use arrow keys, or double-click to reset."
    onPointerDown={(event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      drag.current = { x: event.clientX, width: panel.width, scale: Math.max(.01, pixelsPerUnit?.() ?? 1) };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.setAttribute("data-dragging", "true");
    }}
    onPointerMove={(event) => {
      if (!drag.current) return;
      pending.current = drag.current.width + direction * (event.clientX - drag.current.x) / drag.current.scale;
      if (frame.current === null) frame.current = requestAnimationFrame(flush);
    }}
    onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={(event) => {
      flush(); drag.current = null; event.currentTarget.removeAttribute("data-dragging");
    }}
    onDoubleClick={() => panel.setWidth(panel.initial)}
    onKeyDown={(event) => {
      const delta = event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0;
      if (delta) { event.preventDefault(); panel.setWidth((width) => clamp(width + direction * delta, width, "key")); }
      if (event.key === "Home" || event.key === "End") { event.preventDefault(); panel.setWidth((width) => clamp(event.key === "Home" ? panel.min : panel.max, width, "pointer")); }
    }} />;
}

/** Inline width shortcuts alongside the resize handle, never fullscreen actions.
 * Owners retain width/selection policy; use a distinct fullscreen icon to open expanded content. */
export function PanelFocusControls({ firstLabel, secondLabel, firstActive, secondActive, onFirst, onSecond }: {
  firstLabel: string; secondLabel: string; firstActive: boolean; secondActive: boolean;
  onFirst: () => void; onSecond: () => void;
}) {
  return <div className="celia-panel-focus-controls" role="group" aria-label="Column focus">
    <Tooltip content={`${secondLabel}${secondActive ? " · click to balance columns" : ""}`}><button type="button" className={btn("ghost", "icon")} aria-label={secondLabel} aria-pressed={secondActive} onClick={onSecond}><Glyph name="chevron_left" size={18} /></button></Tooltip>
    <Tooltip content={`${firstLabel}${firstActive ? " · click to balance columns" : ""}`}><button type="button" className={btn("ghost", "icon")} aria-label={firstLabel} aria-pressed={firstActive} onClick={onFirst}><Glyph name="chevron_right" size={18} /></button></Tooltip>
  </div>;
}
